import { FunctionComponent, ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { classNames } from "@/utils/classNames";

import { FROM_TYPE } from "../../../Contacts/schema/contacts";
import { formatDate } from "../../utils/formatDate";

import { priorityClassName, priorityLabelKey } from "./changeLogPriority";
import { groupLead, HistoryGroup, personaChanges } from "./groupHistory";

interface ChangeLogRowProps {
	group: HistoryGroup;
}

const TAG = "mt-1 inline-block rounded-full border px-2 py-0.5 text-[11px]";

/**
 * One manual override in the change log (PREC-4055): the lead, when, what the
 * company and persona classification were before and after, the note, and
 * who made the change — with a link back to the list or the contacts page.
 */
export const ChangeLogRow: FunctionComponent<ChangeLogRowProps> = ({ group }) => {
	const { t } = useTranslation();
	const reasonRef = useRef<HTMLParagraphElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [truncated, setTruncated] = useState(false);

	const lead = groupLead(group);
	const { resultChanged, priorityChanged } = personaChanges(group);
	const fromType = lead.fromType ?? group.persona?.fromType ?? group.company?.fromType;
	const listId = lead.listId ?? group.persona?.listId ?? group.company?.listId;
	const listName = lead.listName ?? group.persona?.listName ?? group.company?.listName;

	useLayoutEffect(() => {
		const element = reasonRef.current;

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setTruncated(!!element && element.scrollHeight > element.clientHeight);
	}, [lead.reason]);

	const priority = (level: string | null | undefined) => (
		<p className={classNames("w-fit rounded-full px-2 py-1 text-xs", priorityClassName(level))}>
			{t(`leadScoring.changeLog.priority.${priorityLabelKey(level)}`)}
		</p>
	);

	const side = (
		result: (entry: NonNullable<HistoryGroup["company"]>) => string,
		updated: boolean
	): ReactNode => (
		<>
			{group.company && (
				<div className={group.persona ? "mb-2 border-b border-border-light pb-2" : ""}>
					<p className={classNames("line-clamp-2 text-sm text-text", updated && "font-medium")}>
						{result(group.company) || "—"}
					</p>
					<span
						className={classNames(
							TAG,
							updated
								? "border-action-300 bg-action-200 text-blue-action"
								: "border-gray-200 bg-gray-100 text-text-meta"
						)}
					>
						{t("leadScoring.changeLog.company")}
					</span>
				</div>
			)}
			{group.persona && (
				<div className="flex flex-col gap-2">
					{resultChanged && (
						<div>
							<p className={classNames("line-clamp-2 text-sm text-text", updated && "font-medium")}>
								{result(group.persona) || "—"}
							</p>
							<span
								className={classNames(
									TAG,
									updated
										? "border-green-500 bg-green-200 text-text-meta"
										: "border-gray-200 bg-gray-100 text-text-meta"
								)}
							>
								{t("leadScoring.changeLog.persona")}
							</span>
						</div>
					)}
					{resultChanged && priorityChanged && <hr className="border-border-light" />}
					{priorityChanged &&
						priority(updated ? group.persona.newOverallScore : group.persona.previousOverallScore)}
				</div>
			)}
		</>
	);

	return (
		<tr
			className="border-b border-border-light last:border-b-0 hover:bg-gray-50/50"
			data-testid="changelog-row"
		>
			<td className="px-4 py-3.5">
				<p className="text-sm font-semibold text-text">{lead.prospectName ?? "—"}</p>
				<p className="mt-0.5 text-xs text-text-meta">{lead.organizationName ?? "—"}</p>
			</td>
			<td className="whitespace-nowrap px-4 py-3.5 text-sm text-text-meta">
				{formatDate(lead.createdAt)}
			</td>
			<td className="max-w-[260px] px-4 py-3.5">
				{side((entry) => entry.previousResult ?? "", false)}
			</td>
			<td className="max-w-[260px] px-4 py-3.5">{side((entry) => entry.newResult, true)}</td>
			<td className="max-w-[240px] px-4 py-3.5 text-sm text-text-meta">
				<p ref={reasonRef} className={expanded ? "overflow-visible" : "line-clamp-2"}>
					{lead.reason || "—"}
				</p>
				{(truncated || expanded) && (
					<button
						className="cursor-pointer text-xs text-blue-action hover:underline"
						type="button"
						onClick={() => setExpanded((current) => !current)}
					>
						{t(expanded ? "leadScoring.changeLog.showLess" : "leadScoring.changeLog.showMore")}
					</button>
				)}
			</td>
			<td className="whitespace-nowrap px-4 py-3.5 text-sm text-text-meta">
				<p className="text-xs">
					{lead.userFirstName} {lead.userLastName}
				</p>
				{fromType === FROM_TYPE.LIST && listId ? (
					<Link
						className="mt-0.5 block text-xs text-blue-action hover:underline"
						to={`/app/lists/detail/${listId}`}
					>
						{t("leadScoring.changeLog.viaList", { name: listName ?? listId })}
					</Link>
				) : fromType === FROM_TYPE.CONTACTS ? (
					<Link
						className="mt-0.5 block text-xs text-blue-action hover:underline"
						to="/app/contacts?tab=CONTACTS"
					>
						{t("leadScoring.changeLog.viaContacts")}
					</Link>
				) : null}
			</td>
		</tr>
	);
};
